import { isPast, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import useLocale from '../../common/useLocale';

interface Promotion {
  start_date?: string | null;
  end_date?: string | null;
  active: boolean;
}

interface Sellable {
  promotions: Promotion[];
  active: boolean;
  is_sold_out: boolean;
  has_usable_invitations?: boolean;
}

interface SellableStatusIndicatorProps {
  sellable: Sellable;
}

const SellableStatusIndicator = ({ sellable }: SellableStatusIndicatorProps) => {
  const { message } = useSellableStatus(sellable);

  return (
    <>
      {message}
    </>
  );
};

export enum SellableStatus {
  Available = 'available',
  SoldOut = 'sold_out',
  ByInvitation = 'by_invitation',
  Unavailable = 'unavailable',
}

export const useSellableStatus = (sellable: Sellable) => {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();

  let status: SellableStatus;
  let message: string;

  // Keep track for promotions that are currently on sale.
  let forSale = false;
  let nextEndDate: Date | null = null;

  // Keep track for promotions that are not currently on sale.
  let lastEndDate: Date | null = null;
  let nextStartDate: Date | null = null;
  let noEndDate = false;

  sellable.promotions.forEach((promotion) => {
    if (promotion.active) {
      const startDate = promotion.start_date
        ? parseDate(promotion.start_date, { timezone: 'UTC' }) : null;
      const endDate = promotion.end_date
        ? parseDate(promotion.end_date, { timezone: 'UTC' }) : null;

      const started = !startDate || isPast(startDate);
      const ended = endDate && isPast(endDate);

      noEndDate = noEndDate || !endDate;

      if (started && !ended) {
        forSale = true;

        if (endDate && (!nextEndDate || endDate > nextEndDate)) {
          nextEndDate = endDate;
        }
      } else if (ended) {
        if (!lastEndDate || endDate > lastEndDate) {
          lastEndDate = endDate;
        }
      } else if (!started) {
        if (!nextStartDate || startDate < nextStartDate) {
          nextStartDate = startDate;
        }

        if (nextEndDate && startDate < nextEndDate && endDate) {
          nextEndDate = endDate;
        }
      }
    }
  });

  if (forSale && sellable.has_usable_invitations && (!sellable.active || sellable.is_sold_out)) {
    status = SellableStatus.ByInvitation;
    message = t('with_invitation_code');
  } else if (sellable.active && !forSale && nextStartDate) {
    status = SellableStatus.Unavailable;

    const startDate = formatDate(
      nextStartDate,
      { format: isToday(nextStartDate) ? 'display_time' : 'display_compact_date' },
    );

    message = t('starts_on', { date: startDate });
  } else if (sellable.active && !forSale && lastEndDate) {
    status = SellableStatus.Unavailable;

    const endDate = formatDate(
      lastEndDate,
      { format: isToday(lastEndDate) ? 'display_time' : 'display_compact_date' },
    );

    message = t('ended_on', { date: endDate });
  } else if (sellable.active && sellable.is_sold_out) {
    status = SellableStatus.SoldOut;
    message = t('sold_out');
  } else if (sellable.active && forSale) {
    status = SellableStatus.Available;

    if (nextEndDate) {
      const endDate = formatDate(
        nextEndDate,
        { format: isToday(nextEndDate) ? 'display_time' : 'display_compact_date' },
      );

      message = t('ends_on_date', { date: endDate });
    } else {
      message = t('available');
    }
  } else {
    status = SellableStatus.Unavailable;

    if (lastEndDate) {
      const endDate = formatDate(
        lastEndDate,
        { format: isToday(lastEndDate) ? 'display_time' : 'display_compact_date' },
      );

      message = t('ended_on', { date: endDate });
    } else {
      message = t('offline');
    }
  }

  return {
    status,
    message,
  };
};

export default SellableStatusIndicator;
